import React, { useState, useEffect } from 'react';
import { CGProduct } from '../../types/shopify';
import { Theme } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/ArrowForward';
import CardMaster from '../Product/CardMaster';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ArrowCircle from '@mui/icons-material/ArrowCircleUpOutlined';
import Container from 'components/Layout/Container';
import { Link as RouterLink } from 'react-router-dom';

// import debug from 'debug';
// const d = debug('cardReveal');

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      flexGrow: 1,
      margin: 'auto',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'flex-end',
      overflow: 'hidden',
      '&:before': {
        content: "''",
        backgroundColor: '#FF6B00',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
      },
    },
    cardMessage: {
      position: 'absolute',
      width: '100%',
      ...theme.typography.h1,
      whiteSpace: 'pre-wrap',
      [theme.breakpoints.down('md')]: {
        overflowY: 'auto',
        fontSize: '20px',
        lineHeight: '24px',
      },
      '&::-webkit-scrollbar': {
        width: 0,
      },
    },
    cardSubMessage: {
      position: 'absolute',
      bottom: theme.spacing(33),
      width: '100%',
      ...theme.typography.h3,
      whiteSpace: 'pre-wrap',
      textAlign: 'center',

      [theme.breakpoints.up('sm')]: {
        bottom: theme.spacing(32),
      },
    },
    dragArea: {
      width: 468,
      maxWidth: '100%',
      marginLeft: 'auto',
      marginRight: 'auto',
      position: 'relative',
    },
    envelope: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      margin: '0 auto',
      width: 'calc(100% - 26px)',
      [theme.breakpoints.up('sm')]: {
        width: '100%',
      },
      height: 225,
      background: 'radial-gradient(circle at center top, transparent 50px, #222 0)',
      backgroundSize: 'cover',
      backgroundPosition: 'center top',
      zIndex: 20,
      opacity: 1,
    },
    cardHandle: {
      position: 'absolute',
      width: '100%',
      marginBottom: 0,
      zIndex: 30,
      cursor: 'grab !important',
    },
    cardContainer: {
      position: 'absolute',
      width: '100%',
      padding: theme.spacing(0, 0, 0, 0),
      zIndex: 10,
    },
    card: {
      pointerEvents: 'inherit',
      cursor: 'grab !important',
      '&.Mui-disabled': {
        pointerEvents: 'inherit',
      },
    },
    helpContainer: {
      position: 'absolute',
      margin: 'auto',
      width: '100%',
      bottom: 300,
    },
    help: {
      ...theme.typography.h3,
    },
    descriptionContainer: {
      margin: '0 auto',
      padding: theme.spacing(0, 1),
      width: '100%',
      maxWidth: 700,
      textAlign: 'center',
      marginBottom: -100,

      [theme.breakpoints.up('sm')]: {
        marginBottom: -130,
      },
    },
    description: {
      ...theme.typography.h1,
    },
    sun: {
      backgroundColor: '#FFA61F',
      position: 'absolute',
      width: 100,
      height: 100,
      borderRadius: '50%',
      left: '50vw',
      top: '75vh',
      transform: 'translate(-50%, -50%)',
    },
    sunAlt: {
      backgroundColor: '#FFA61F',
      position: 'absolute',
      width: 100,
      height: 100,
      borderRadius: '50%',
      left: '50vw',
      top: '75vh',
      transform: 'translate(-50%, -50%)',
    },
    button: {
      position: 'absolute',
      bottom: theme.spacing(5),
      zIndex: 90,
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
    },
    debug: {
      zIndex: 1000,
      position: 'absolute',
      top: '50vh',
      width: '100%',
      backgroundColor: 'red',
      height: 1,
      display: 'none',
    },
  }),
  { name: 'CardReveal' },
);

interface CardRevealProps {
  item?: CGProduct;
  cardImageURL?: string;
  message?: string;
  helperText?: string;
  description?: string;
  // cardValue?: number;
  isCompact?: boolean; // compact mode used on homepage
  buttonText?: string;
}

const CardReveal = (props: CardRevealProps) => {
  const {
    item, // The Product...
    message = '',
    helperText = 'Click or drag me',
    description,
    cardImageURL,
    // cardValue,
    isCompact = false,
    buttonText = 'Personalise a Giftcard',
  } = props;
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const cardHeight = 300;
  const windowHeight = isCompact ? 849 : window.innerHeight;
  const dragHeight = windowHeight / 2 + cardHeight / 2 + (isCompact && isDesktop ? 50 : 0);

  const [runAnim, setRunAnim] = useState<boolean>(false);

  const [dragOffset, setDragOffset] = useState<any>({ x: 0, y: dragHeight - cardHeight });
  useEffect(() => {
    setDragOffset({
      x: 0,
      y: dragHeight - cardHeight,
    });
  }, [dragHeight]);

  const classes = useStyles();
  const onDragHandler: DraggableEventHandler = (ev, data) => {
    //cannot drag down during compact mode
    if (isCompact && data.lastY === 0) return false;
    setDragOffset({
      x: data.x,
      y: data.y,
    });
  };
  const maxValue = dragHeight - cardHeight;
  const percentage = (maxValue - dragOffset.y) / maxValue;
  useEffect(() => {
    const animLength = 600;
    const fps = 60;
    const intervalMS = 1000 / fps;
    if (runAnim) {
      let interval: any;
      let runTime = 0;
      interval = setInterval(() => {
        runTime += intervalMS;
        const runTimePercentage = runTime / animLength;
        setDragOffset({
          x: 0,
          y: (1 - runTimePercentage) * maxValue,
        });
        if (runTime >= animLength) {
          setRunAnim(false);
          clearInterval(interval);
        }
      }, intervalMS);
      return () => {
        clearInterval(interval);
      };
    }
  }, [runAnim, maxValue]);
  return (
    <>
      <div className={classes.debug} />
      <Box
        className={classes.root}
        sx={{
          '&:before': {
            opacity: percentage,
          },
          height: isCompact ? (isDesktop ? '750px' : '680px') : 'calc(100vh)',
        }}
      >
        <Box
          className={classes.sun}
          style={{
            width: percentage * window.innerWidth,
            height: percentage * window.innerWidth,
            opacity: Math.sin(percentage * Math.PI),
          }}
        />
        <Box
          className={classes.sunAlt}
          style={{
            width: percentage * windowHeight * 0.75,
            height: percentage * windowHeight * 0.75,
            opacity: Math.sin(percentage * Math.PI),
          }}
        />
        <Box
          className={classes.cardMessage}
          sx={{
            opacity: percentage,
            height: `calc(100vh - 60px - ${dragHeight}px)`,
            ...(isCompact ? { textAlign: 'center', top: 75 } : { top: theme.spacing(8) }),
          }}
        >
          <Container>{message}</Container>
        </Box>

        {isCompact && (
          <Box className={classes.cardSubMessage} sx={{ opacity: percentage }}>
            <Box sx={{ maxWidth: 400, margin: '0 auto' }}>A unique unboxing experience for anyone, anywhere! Send instantly or schedule for later.</Box>
          </Box>
        )}

        {isCompact && (
          <Box className={classes.button} sx={{ opacity: percentage }}>
            <Button
              component={RouterLink} // It is a link...
              to={'/corporate'}
              size="medium"
              color="secondary"
              variant="outlined"
              endIcon={<SendIcon />}
            >
              {buttonText}
            </Button>
          </Box>
        )}

        {description && (
          <Stack
            alignItems="center"
            className={classes.descriptionContainer}
            style={{
              opacity: 1 - percentage * 3,
            }}
          >
            <Box className={classes.description}>{description}</Box>
          </Stack>
        )}
        <Box
          className={classes.dragArea}
          sx={{
            height: dragHeight,
          }}
        >
          <Stack
            alignItems="center"
            className={classes.helpContainer}
            style={{
              opacity: 1 - percentage * 3,
            }}
          >
            <Box className={classes.help}>{helperText}</Box>
            <IconButton onClick={() => setRunAnim(true)} size="large">
              <ArrowCircle />
            </IconButton>
          </Stack>
          <div className={classes.envelope} />
          <Draggable axis="y" bounds="parent" position={dragOffset} onDrag={onDragHandler}>
            <div
              className={classes.cardHandle}
              style={{
                height: cardHeight,
              }}
            />
          </Draggable>
          <div
            className={classes.cardContainer}
            style={{
              height: cardHeight,
              top: dragOffset.y,
              padding: 24,
            }}
          >
            <CardMaster
              noButton
              item={item}
              className={classes.card}
              image={cardImageURL}
              // showPrice={cardValue}
            />
          </div>
        </Box>
      </Box>
    </>
  );
};

export default CardReveal;
